import { ALCmodelHeader } from "@/constants/columnHeader";
import { queryDefaultValues } from "@/constants/queryDefaultValues";
import { ALCModelCreateForm } from "@/features/alcModel/form/createForm";
import { ALCModelUpdateForm } from "@/features/alcModel/form/updateForm";
import { useModal } from "@/features/modal/ModalStackManager";
import { ActionHeader } from "@/features/standard/ActionHeader";
import { Header } from "@/features/standard/Header";
import { Main } from "@/features/standard/Main";
import { HeaderSubTitle } from "@/features/standard/SubTitle";
import { HeaderTitle } from "@/features/standard/Title";
import { Wrapper } from "@/features/standard/Wrapper";
import customAlert from "@/features/ui/alert/Alert";
import { CustomFilter } from "@/features/ui/Base/List/CustomFilter/CustomFilter";
import { CustomSorter } from "@/features/ui/Base/List/CustomSorter/CustomSorter";
import { SearchBox, SearchProps } from "@/features/ui/Base/List/SearchBox/SearchBox";
import useQueryString from "@/hooks/useQueryString";
import { css } from "@emotion/css";
import styled from "@emotion/styled";
import { Button, Flex, Pagination, TextInput } from "@mantine/core";
import { IconEdit, IconPlus, IconTrash } from "@tabler/icons-react";
import { useRef, useState } from "react";
import DataGrid, { Column, SelectColumn } from "react-data-grid";

const ALCModelWrap = Object.assign({}, Main, {
  Header: Header,
  HeaderTitle: HeaderTitle,
  HeaderSubTitle: HeaderSubTitle,
  Wrapper: Wrapper,
  ActionHeader: ActionHeader,
  ActionButton: Button,
  CreateForm: ALCModelCreateForm,
  UpdateForm: ALCModelUpdateForm,
});

export const ALCModelPage = () => {
  const { openModal } = useModal();
  const [initialQueryString, setQueryString] = useQueryString(queryDefaultValues);

  const [activePage, setPage] = useState(initialQueryString.page);
  const [query, setQuery] = useState(JSON.parse(initialQueryString.query));
  const [sort, setSort] = useState(initialQueryString.sort);
  const [search, setSearch] = useState<SearchProps>({
    search: initialQueryString.search,
    searchFields: initialQueryString.searchFields,
    pageSize: initialQueryString.pageSize,
  });

  const selectedRowsRef = useRef<ReadonlySet<any>>(new Set());
  const [selectedRows, setSelectedRows] = useState((): ReadonlySet<any> => new Set());

  const searchFieldsHeader = ALCmodelHeader.filter(
    (item) => item.category === "text" && !item.isEnum,
  );

  const pageSize = 10;
  const totalRows = PeripheralPageMockupData.length;

  // 페이지에 맞는 데이터 슬라이싱
  const paginatedRows = PeripheralPageMockupData.slice(
    (activePage - 1) * pageSize,
    activePage * pageSize,
  );

  const columns: readonly Column<any>[] = [
    {
      ...SelectColumn,
      width: 70,
      maxWidth: 500,
      resizable: true,
      headerCellClass: css`
        & > * {
          justify-content: flex-start;
          padding-left: 24px;
        }
      `,
      cellClass: css`
        .rdg-checkbox-label {
          padding-left: 24px;
        }
      `,
    },
    {
      key: "partnerCode",
      name: "거래처(필수)",
      sortable: true,
      resizable: true,
    },
    {
      key: "siteSeq",
      name: "사업자 번호",
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return <TextInput defaultValue={formatterProps.row.siteSeq} placeholder="자동입력" />;
      },
    },
    {
      key: "modelName",
      name: "모델(필수)",
      sortable: true,
      resizable: true,
    },
    {
      key: "options",
      name: "액션",
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <OptionBox>
            <OptionBtn
              variant="subtle"
              leftIcon={<IconEdit />}
              color="yellow"
              onClick={() => {
                openModal(
                  <ALCModelWrap.UpdateForm formatterProps={formatterProps.row} />,
                  {},
                  "주변장치 수정",
                  true,
                );
              }}
            ></OptionBtn>
            <OptionBtn
              variant="subtle"
              leftIcon={<IconTrash />}
              color="red"
              onClick={() => {
                window.confirm("정말로 삭제하시겠습니까?") &&
                  customAlert("주변장치 삭제에 실패하였습니다.", "삭제 실패", "red");
              }}
            ></OptionBtn>
          </OptionBox>
        );
      },
    },
  ];

  const deleteSelectedItems = () => {
    window.confirm("정말로 삭제하시겠습니까?") &&
      customAlert("선택된 행 삭제에 실패하였습니다.", "삭제 실패", "red");
  };

  return (
    <ALCModelWrap.Wrapper>
      <ALCModelWrap.ActionHeader>
        <ALCModelWrap.ActionButton
          leftIcon={<IconPlus />}
          onClick={() => openModal(<ALCModelWrap.CreateForm />, null, "모델 추가", true)}
        >
          추가
        </ALCModelWrap.ActionButton>
        <ALCModelWrap.ActionButton
          color="red"
          rightIcon={<IconTrash />}
          onClick={() => deleteSelectedItems()}
        >
          선택된 행 삭제
        </ALCModelWrap.ActionButton>
      </ALCModelWrap.ActionHeader>
      <ALCModelWrap.Header>
        <ALCModelWrap.HeaderTitle>ALC모델 관리</ALCModelWrap.HeaderTitle>
        <ALCModelWrap.HeaderSubTitle>
          {"우리 회사에서 다루는 ALC모델에 대해 조회할 수 있는 페이지 입니다."}
        </ALCModelWrap.HeaderSubTitle>
      </ALCModelWrap.Header>
      <Flex w="100%" justify="space-between">
        <Flex direction="row" gap="xs" justify="flex-start" align="flex-center">
          <CustomFilter filterType={ALCmodelHeader} setQuery={setQuery} query={query} />
          <CustomSorter sorterType={ALCmodelHeader} setSort={setSort} sort={sort} />
        </Flex>
        <SearchBox searchType={searchFieldsHeader} setSearch={setSearch} search={search} />
      </Flex>
      <Flex w="100%" h="100%">
        <GridWrapper>
          <DataGrid
            columns={columns ?? []}
            rows={paginatedRows}
            rowHeight={40}
            rowKeyGetter={(row) => row.logDate}
            selectedRows={selectedRows}
            onSelectedRowsChange={(e) => {
              setSelectedRows(e);
              const newSelectedRows = new Set(selectedRowsRef.current);
              newSelectedRows.add(e);
              selectedRowsRef.current = newSelectedRows;
            }}
          />
          <Pagination
            onChange={setPage}
            value={activePage}
            total={Math.ceil(totalRows / pageSize)} // 전체 페이지 계산
            size="lg"
            radius="sm"
            position="center"
          />
        </GridWrapper>
      </Flex>
    </ALCModelWrap.Wrapper>
  );
};

const PeripheralPageMockupData = [
  {
    partnerCode: "린나이",
    siteSeq: "33212133",
    modelName: "RTR-R2100",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "RFA-8SE",
  },
  {
    partnerCode: "태한",
    siteSeq: "33518498",
    modelName: "TEH-공용",
  },
  {
    partnerCode: "쿠쿠전자",
    siteSeq: "",
    modelName: "전 TOOL 공용",
  },
  {
    partnerCode: "린나이",
    siteSeq: "12232441",
    modelName: "V.KGS 4P-S",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "공용",
  },
  {
    partnerCode: "파세코",
    siteSeq: "31548498",
    modelName: "WKH-707S",
  },
  {
    partnerCode: "린나이",
    siteSeq: "33948198",
    modelName: "V041S-0104",
  },
  {
    partnerCode: "린나이",
    siteSeq: "33955718",
    modelName: "V043S-0333(비례전자벨브)",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "V043S-0163(비례전자벨브)",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "V043S-0144(비례전자벨브)",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "V031D",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "V028S-0044(비례전자벨브)",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "V028S-0033(비례전자벨브)",
  },
  {
    partnerCode: "린나이",
    siteSeq: "33966428",
    modelName: "V028S-0023(비례전자벨브)",
  },
  {
    partnerCode: "린나이",
    siteSeq: "33956412",
    modelName: "V020Z-160",
  },
  {
    partnerCode: "린나이",
    siteSeq: "12355718",
    modelName: "V013S-0183",
  },
  {
    partnerCode: "린나이",
    siteSeq: "33956842",
    modelName: "V.OT GOV-S",
  },
  {
    partnerCode: "파세코",
    siteSeq: "33945122",
    modelName: "TORPEDO",
  },
  {
    partnerCode: "태한",
    siteSeq: "",
    modelName: "TEH-R302",
  },
  {
    partnerCode: "태한",
    siteSeq: "",
    modelName: "TEH-R202",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "SWR-A31CDT",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "SWR-402S",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "SWR-301",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "SWO-612",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "SWF-03HD",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "SWF-02HD",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "SWE-31H",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "SWE-12H",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "SWO-612",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "SBO-D6000",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "SBO-5121(3021)",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "SBO-5121",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "SBO-3021",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "RW-24BF",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "RW-14BF",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "RTR-Y3200",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "RTR-Y3000",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "RTR-X2200",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "RTR-R2100(RTB-A210)",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "RTR-R2000",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "RTR-Q3200",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "RTR-Q310",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "RTR-K320GT",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "RTR-J330L",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "RTR-J330C",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "RTR-J328C",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "RTR-J320W",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "RTR-J3101",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "RTR-J3100",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "RTR-H2100",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "RTR-G320W",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "RTR-F3300",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "RTR-F3100",
  },
  {
    partnerCode: "린나이",
    siteSeq: "",
    modelName: "RTR-E320W",
  },
];

const GridWrapper = styled.div`
  & *[role="grid"] {
    height: 440px;
    // --rdg-background-color: {(props) => (props.isDarkMode ? "white" : "none")};
    // --rdg-header-background-color: {(props) =>
    // props.isDarkMode ? "white" : "none"};
    // --rdg-color: {(props) => (props.isDarkMode ? "black" : "white")};
    // --rdg-row-hover-background-color: {(props) =>
    // props.isDarkMode ? "#f5f5f5" : theme?.colors?.gray?.[7]};
  }
  & *[role="columnheader"] {
    // color: #7d8fa9;
    font-size: 12px;
    font-family: Roboto;
    font-weight: 500;
    word-wrap: break-word;
    // border: none;
    box-shadow: none;
    display: flex;
    align-items: center;
  }
  & *[aria-colindex="1"] {
  }

  & *[role="row"] {
    height: 100px;
  }

  & *[role="gridcell"] {
    display: flex;
    align-items: center;
    // border-left: none;
    // border-right: none;
    box-shadow: none;

    & > * {
      justify-content: flex-start;
    }
  }
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: space-between;
  gap: 1rem;
`;

const OptionBox = styled.fieldset`
  display: flex;
  gap: 10px;
  border: none;
`;

const OptionBtn = styled<any>(Button)`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
